import URI from 'urijs';

// Validates that a URL starts with http://, https://, or www.
const URL_VALIDATE_REGEX = /^(?:https?:\/\/|www\.)/i;

// Used for fallback regex detection when URI.js finds nothing.
// Matches http(s)://... and www.... URLs, avoiding consuming trailing punctuation.
// NOTE: reset lastIndex before each use due to the global flag.
const URL_MATCH_REGEX = /\b((?:https?:\/\/|www\.)[^\s<>"{}|\\^`[\]]*[^\s<>"{}|\\^`[\].,;:!?()'"])/gi;

// Validates that a hostname is a well-formed domain (not an IP-like or invalid string).
const DOMAIN_REGEX = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

// Trailing punctuation characters to strip from the end of fallback-detected URLs.
const TRAILING_PUNCT = /[.,;:!?)'"]+$/;

/**
 * Builds a React <a> element for a detected URL.
 * Always opens in a new tab with rel="noopener noreferrer".
 */
function buildAnchor(href, displayText, key) {
  return (
    <a
      key={key}
      className="message-link"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {displayText}
    </a>
  );
}

/**
 * Linkify a plain text string, replacing detected URLs with React <a> elements.
 *
 * Mirrors the two-step approach from TextBuilderHelper.js:
 *   Step 1 (primary):  URI.withinString() from the urijs library finds and validates URLs.
 *   Step 2 (fallback): A regex scan catches any URLs that URI.js missed or rejected.
 *
 * - Bare `www.` URLs are given an `http://` prefix in the href.
 * - Trailing punctuation (. , ; : ! ? ) ' ") is excluded from URLs in the fallback path.
 * - All links open in a new tab with rel="noopener noreferrer".
 *
 * @param {string} text - Plain text that may contain URLs.
 * @returns {Array<string|React.ReactElement>} Mixed array suitable for React rendering.
 */
function linkify(text) {
  if (text == null) return [];
  if (text === '') return [''];

  /** @type {Array<[number, number]>} start/end char positions of each detected URL */
  const urlPositions = [];
  /** @type {Array<React.ReactElement>} corresponding <a> elements */
  const aTags = [];

  /** Set of character positions already covered by URI.js-detected URLs */
  const coveredPositions = new Set();

  // ── Step 1: URI.js primary detection ──────────────────────────────────────
  URI.withinString(text, (url, start, end) => {
    // Prepend http:// for bare www. URLs so the href is always absolute.
    const href = url.startsWith('www.') ? 'http://' + url : url;

    // Reject anything that doesn't look like an http/https/www link.
    if (!URL_VALIDATE_REGEX.test(href)) {
      // Mark the range as seen by URI.js even though it was rejected,
      // so the fallback can still attempt to match it independently.
      return url;
    }

    // Validate the hostname to avoid matching malformed strings.
    let host;
    try {
      host = new URI(href).hostname();
    } catch {
      return url;
    }
    if (!host || host.length > 253 || !DOMAIN_REGEX.test(host)) {
      return url;
    }

    // Mark positions as covered so the fallback skips them.
    for (let p = start; p < end; p++) coveredPositions.add(p);
    urlPositions.push([start, end]);
    aTags.push(buildAnchor(href, url, `link-${start}`));
    return url;
  });

  // ── Step 2: Regex fallback (catches URLs that URI.js missed) ──────────────
  URL_MATCH_REGEX.lastIndex = 0;
  let match;
  while ((match = URL_MATCH_REGEX.exec(text)) !== null) {
    // Skip URLs already detected by URI.js in Step 1.
    if (coveredPositions.has(match.index)) continue;

    let url = match[0];
    // Strip trailing punctuation that was swept into the match.
    url = url.replace(TRAILING_PUNCT, '');

    const href = url.startsWith('www.') ? 'http://' + url : url;

    // Apply the same domain validation as the primary path.
    let host;
    try {
      host = new URI(href).hostname();
    } catch {
      continue;
    }
    if (!host || host.length > 253 || !DOMAIN_REGEX.test(host)) {
      continue;
    }

    const start = match.index;
    const end = start + url.length;

    urlPositions.push([start, end]);
    aTags.push(buildAnchor(href, url, `link-${start}`));
  }

  // ── Sort by position (results from both paths may interleave) ───────────
  if (urlPositions.length > 1) {
    const indices = urlPositions.map((_, i) => i);
    indices.sort((a, b) => urlPositions[a][0] - urlPositions[b][0]);
    const sortedPositions = indices.map((i) => urlPositions[i]);
    const sortedTags = indices.map((i) => aTags[i]);
    urlPositions.length = 0;
    aTags.length = 0;
    urlPositions.push(...sortedPositions);
    aTags.push(...sortedTags);
  }

  // ── Assemble result: plain text segments interleaved with <a> elements ─────
  if (aTags.length === 0) {
    return [text];
  }

  const result = [];
  for (let i = 0; i < aTags.length; i++) {
    const segStart = i === 0 ? 0 : urlPositions[i - 1][1];
    const textBefore = text.substring(segStart, urlPositions[i][0]);
    if (textBefore) result.push(textBefore);
    result.push(aTags[i]);
  }

  const trailing = text.substring(urlPositions[urlPositions.length - 1][1]);
  if (trailing) result.push(trailing);

  return result;
}

export { linkify };
